import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import type { Chart } from 'chart.js';
import { circularDefaults, legendBottom } from '../../utils/chartDefaults';
import {
    calculatePercentage,
    deepMerge,
    formatNumber,
    getChartColors,
    isMissingColor,
} from '../../utils/chartHelpers';
import type { ChartDataLike } from '../../utils/chartHelpers';
import ChartFrame from './ChartFrame';

const applyCircularTheme = (data: ChartDataLike): ChartDataLike => {
    if (!data || !Array.isArray(data.datasets) || data.datasets.length === 0) return data;
    const labelsLength = Array.isArray(data.labels) ? data.labels.length : 0;
    const datasets = data.datasets.map((ds) => {
        const next: any = { ...ds };
        if (isMissingColor(ds.backgroundColor)) {
            next.backgroundColor = getChartColors(Math.max(1, labelsLength));
        }
        return next;
    });
    return { ...data, datasets };
};

const isHalfGaugeData = (data: ChartDataLike): boolean =>
    (data.datasets ?? []).some((ds) => Number(ds.circumference) === 180);

/** Built-in bottom legend for full doughnuts (label, value, %). */
const builtInPercentageLegend = (overrides: Record<string, any> = {}): Record<string, any> => {
    const { display: _display, labels: overrideLabels, ...rest } = overrides;

    return {
        ...legendBottom,
        ...rest,
        display: true,
        labels: {
            ...legendBottom.labels,
            ...(overrideLabels || {}),
            generateLabels(chart: Chart<'doughnut'>) {
                const dataset = chart.data.datasets[0];
                if (!dataset) return [];

                const meta = chart.getDatasetMeta(0);
                const labels = chart.data.labels ?? [];
                const values = (dataset.data ?? []).map((v) => Number(v) || 0);
                const total = values.reduce((sum, v) => sum + v, 0);

                return values.map((value, i) => {
                    const label = String(labels[i] ?? '');
                    const pct = calculatePercentage(value, total);
                    const text = `${label}: ${formatNumber(value)} (${pct}%)`;
                    const style = meta.controller.getStyle(i, false);

                    return {
                        text,
                        fillStyle: style.backgroundColor,
                        strokeStyle: style.borderColor,
                        lineWidth: style.borderWidth,
                        hidden: !chart.getDataVisibility(i),
                        index: i,
                        datasetIndex: 0,
                    };
                });
            },
        },
    };
};

const shouldShowBuiltInLegend = (
    data: ChartDataLike,
    showLegend: boolean | undefined,
    options: any,
): boolean => {
    if (showLegend === true) return true;
    if (isHalfGaugeData(data)) return false;
    if (showLegend === false) return false;
    if (options?.plugins?.legend?.display === false) return false;
    if (typeof options?.plugins?.legend?.labels?.generateLabels === 'function') return false;
    return true;
};

const mergeOptionsWithLegend = (
    options: any,
    data: ChartDataLike,
    showLegend: boolean | undefined,
): any => {
    const base = deepMerge(circularDefaults, options || {});
    if (!shouldShowBuiltInLegend(data, showLegend, options)) return base;

    const userLegend = options?.plugins?.legend ?? {};
    const legendConfig = builtInPercentageLegend(userLegend);

    return {
        ...base,
        layout: deepMerge(base.layout ?? {}, { padding: { bottom: 12 } }),
        plugins: {
            ...base.plugins,
            legend: legendConfig,
        },
    };
};

export interface DoughnutChartProps {
    data: ChartDataLike;
    options?: any;
    className?: string;
    title?: string;
    badge?: string;
    /**
     * Bottom legend with label, value, and share (%).
     * Defaults to true for full doughnuts; always false for half-circle gauges unless true.
     */
    showLegend?: boolean;
}

const DoughnutChart: React.FC<DoughnutChartProps> = ({
    data,
    options,
    className,
    title,
    badge,
    showLegend,
}) => {
    const themed = React.useMemo(() => applyCircularTheme(data), [data]);
    const merged = React.useMemo(
        () => mergeOptionsWithLegend(options, themed, showLegend),
        [options, themed, showLegend],
    );

    return (
        <ChartFrame title={title} badge={badge} className={className}>
            <Doughnut data={themed as any} options={merged} />
        </ChartFrame>
    );
};

export default DoughnutChart;
